import { useLazyQuery, useReactiveVar } from "@apollo/client";
import { useSearch } from "@tanstack/react-router";

import { useEffect, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import LoadingAnimation from "@/components/loading-animation";
import { FacetTypeEnum } from "@/components/pagination";
import ReviewModal from "@/components/review-modal";
import type { ReviewType } from "@/components/review-modal/types";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { user as userStore } from "@/store";

import CategoriesFilter from "../components/categories-filter";
import SortButton from "../components/sort-button";
import SubcategoriesFilter from "../components/subcategories-filter";
import { selectedSortOption } from "../games";
import Layout from "../layout";
import {
  type FacetsDataType,
  GameSortOptionsEnum,
  type PaginatedReviews,
  type ReviewsDataType,
  type ReviewsVariablesType,
  type UserType,
} from "../types";
import Empty from "./empty";
import { QUERY } from "./graphql";
import ReviewItem from "./review-item";

export const PAGE_SIZE = 10;

export interface ReviewedGamesProps {
  facets: FacetsDataType;
  games: PaginatedReviews;
  review?: ReviewType;
  user: UserType;
  variables: ReviewsVariablesType;
}

export default function Reviews({
  facets: facetsData,
  games: serverGames,
  review,
  user: serverUser,
  variables: initialVariables,
}: ReviewedGamesProps) {
  const user = useReactiveVar(userStore);
  const { isMe, isUser, queryContext } = useAuth();
  const [fetchUserReviews, { data, fetchMore, loading }] = useLazyQuery<
    ReviewsDataType,
    ReviewsVariablesType
  >(QUERY);
  const query = useSearch({ strict: false }) as Record<
    string,
    string | string[] | undefined
  >;
  const filterSlug = (initialVariables.filters?.categorySlugs || [""])[0];
  const [category, subcategory] = filterSlug
    ? filterSlug.split("/")
    : [undefined, undefined];

  const variables = useMemo(() => {
    const vars = {
      ...initialVariables,
      includeReview: false,
      isSSR: false,
      isUser,
      reviewId: "",
      sortBy: selectedSortOption(query, initialVariables.sortBy),
    };

    // MY_RATING requires authenticated user
    if (vars.sortBy === GameSortOptionsEnum.MY_RATING && !isUser) {
      vars.sortBy = initialVariables.sortBy as GameSortOptionsEnum;
    }

    return vars;
  }, [isUser, initialVariables, query]);

  const handleLoadMore = () => {
    const currentPage = data?.games.page || serverGames.page;
    if (data !== undefined && !loading) {
      fetchMore({ variables: { ...variables, page: currentPage + 1 } });
    }
  };

  useEffect(() => {
    fetchUserReviews(queryContext(variables));
  }, [queryContext, fetchUserReviews, variables]);

  const hasMorePages = useMemo(() => {
    if (loading) {
      return false;
    }
    if (data) {
      return !!data.games.hasNext;
    }
    return !!serverGames.hasNext;
  }, [loading, data, serverGames]);
  const isCurrentUser = useMemo(
    () => user?.id === serverUser.id,
    [user?.id, serverUser.id]
  );
  const games = useMemo(
    () => (data?.games || serverGames).objects,
    [data, serverGames]
  );

  const facets = useMemo(
    () =>
      facetsData.facets?.find((facet) => facet.type === FacetTypeEnum.CATEGORY)
        ?.items || [],
    [facetsData.facets]
  );
  const categoryFacets = useMemo(
    () => facets.filter((facet) => !facet.parentId),
    [facets]
  );
  const allCount = useMemo(
    () => categoryFacets.reduce((accum, facet) => accum + facet.count, 0),
    [categoryFacets]
  );
  const subcategoryFacets = useMemo(() => {
    if (!category) {
      return [];
    }

    const parent = facets.find(
      (facet) => !facet.parentId && facet.slug === category
    );

    if (!parent) {
      return [];
    }

    return facets.filter((facet) => facet.parentId === parent.id);
  }, [facets, category]);
  const sortHref = useMemo(() => {
    let href = `/@${serverUser.username}/reviews`;

    const categorySlug = (variables.filters?.categorySlugs || [""])[0];

    if (categorySlug) {
      href += `/${categorySlug}`;
    }

    href += "?sortBy=";

    return href;
  }, [serverUser.username, variables.filters?.categorySlugs]);

  return (
    <Layout title="Reviews" user={data?.user || serverUser}>
      <div className="flex flex-col md:flex-col-reverse">
        <div>
          <CategoriesFilter
            count={allCount}
            facets={categoryFacets}
            selectedCategory={category}
            urlPrefix={`/@${serverUser.username}/reviews`}
          />
          {!!subcategoryFacets.length && (
            <SubcategoriesFilter
              facets={subcategoryFacets}
              selectedCategory={subcategory}
              urlPrefix={`/@${serverUser.username}/reviews/${category}`}
            />
          )}
        </div>
        <div className="mb-5 flex h-7 items-center justify-between md:h-7.5">
          <h1 className="mb-5 hidden font-medium text-18/[1.5em] text-white md:block">
            Reviews
          </h1>
          <div className="ml-auto flex items-center gap-2">
            <p className="hidden font-medium text-12/[1.5em] text-white md:block">
              Sort by
            </p>
            <SortButton
              href={sortHref}
              isCurrentUser={isMe(serverUser)}
              sortBy={variables.sortBy as GameSortOptionsEnum}
            />
          </div>
        </div>
      </div>
      {games.length ? (
        <InfiniteScroll
          dataLength={games.length}
          hasMore={hasMorePages}
          loader={<LoadingAnimation />}
          next={handleLoadMore}
        >
          <section
            className={cn(
              "mt-7.5 flex flex-col items-stretch",
              "*:not-first:mt-7.5 *:not-first:border-white/10 *:not-first:border-t *:not-first:pt-7.5"
            )}
          >
            {games.map((game) => (
              <ReviewItem
                game={game}
                isCurrentUser={isCurrentUser}
                key={game.id}
                user={serverUser}
              />
            ))}
          </section>
        </InfiniteScroll>
      ) : (
        <Empty isCurrentUser={isCurrentUser} username={serverUser.username} />
      )}
      {!!review && (
        <ReviewModal
          backUrl={`/@${serverUser.username}/reviews`}
          review={review}
        />
      )}
    </Layout>
  );
}
